import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { BehaviorSubject, Observable, Subscription, map, take } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { CustomPromptsManagementDialogComponent }
    from '../../../Admin/AccountManagement/components/CustomPromptsManagement/custom-prompts-management-dialog.component';
import { CurationActionTypeEnum } from '../../../api/agents/models/curation-agent.api';
import { AppearCondition, AutocompleteCustomOption }
    from '../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { mentionInsightTypesOptions } from '../../../Common/Forms/data/mention-insight-types-options';
import { promptLanguageOptions } from '../../../Common/Forms/data/prompt-language-options';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { ChipModel } from '../../../Common/Forms/models/chip.model';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { MentionInsightTypes } from '../../../Common/Mentions/enums/mention-insight-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { TagModel } from '../../../Common/Tags/models/tag.model';
import { TagsService } from '../../../Common/Tags/services/tags.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { FolderService } from '../../../Monitor/Core/services/folder.service';
import { RSSFeedManageDialogComponent } from '../../../Monitor/Shared/components/RSSFeedManageDialog/rss-feed-manage-dialog.component';
import { Folder } from '../../../Monitor/Shared/models/folder.model';
import { CustomInsightType } from '../../../Monitor/Shared/models/custom-insights-type.model';
import { RSSFeed } from '../../../Monitor/Shared/models/rss-feed.model';
import { CustomInsights } from '../../../Monitor/Shared/models/custom-insights.model';
import { CoverageAnalysisService, InsightContext } from '../../../Monitor/Shared/services/coverage-analysis.service';
import { RSSFeedService } from '../../../Monitor/Shared/services/rss-feed.service';
import {
    folderActionTypes,
    savesToAccountActionTypes,
    selectableActionTypes,
    singleUseActionTypes,
    tagActionTypes
} from '../../enums/curation-agent-action-type.enum';
import {
    CurationOutcomeEnum,
    curationOutcomeLabels,
    curationOutcomes
} from '../../enums/curation-outcome.enum';
import {
    contextReadingInsightTypes,
    CurationAgentActionModel
} from '../../models/curation-agent-action.model';

/** Shape the autocompletes suggest over - `name` is what the user searches and sees */
interface NamedOption<T> {
    id: T;
    name: string;
}

/** What an insight is written in unless another language is picked, as the briefing blocks default */
const defaultInsightLanguage: string = 'en-US';

/** Both of an agent's action lists, as the panel holds them */
export interface CurationOutcomeActions {
    positive: CurationAgentActionModel[];
    negative: CurationAgentActionModel[];
}

@Component({
    selector: 'agpr-curation-agent-actions',
    templateUrl: './curation-agent-actions.component.html',
    styleUrls: ['./curation-agent-actions.component.scss'],
    standalone: false
})
export class CurationAgentActionsComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;
    readonly actionTypes = CurationActionTypeEnum;

    readonly outcomes = CurationOutcomeEnum;
    readonly outcomeList: CurationOutcomeEnum[] = curationOutcomes;

    @Input() id: string = 'curationAgentActions';
    /** Run when a mention matches the agent's criteria */
    @Input() positiveActions: CurationAgentActionModel[] = [];
    /** Run when it does not */
    @Input() negativeActions: CurationAgentActionModel[] = [];
    /** Needed so the manage dialogs opened from here render over the same container as the panel */
    @Input() overlayContainer: Element;

    @Output() actionsChanged: EventEmitter<CurationOutcomeActions> = new EventEmitter<CurationOutcomeActions>();
    /** The Add New Action card has opened at the foot of the list, which the panel scrolls to */
    @Output() addCardOpened: EventEmitter<void> = new EventEmitter<void>();

    private readonly allActionTypeOptions: DropdownOptionModel[] = selectableActionTypes.map(
        type => new DropdownOptionModel(`AutomationAgents.action.${type}`, type)
    );

    /** What the open form offers: the types its outcome does not already have. Set as the form opens */
    actionTypeOptions: DropdownOptionModel[] = this.allActionTypeOptions;

    /** The platform's own insight list, so an agent generates exactly what a mention insight does */
    readonly insightTypeOptions: DropdownOptionModel[] = mentionInsightTypesOptions;
    readonly languageOptions: DropdownOptionModel[] = promptLanguageOptions;
    readonly insightTypes = MentionInsightTypes;

    folderList$: Observable<Folder[]>;
    tagList$: Observable<TagModel[]>;
    /** What the pickers offer: the lists less the targets other actions of the draft's type already have */
    folderSuggestions$: Observable<Folder[]>;
    tagSuggestions$: Observable<TagModel[]>;
    rssFeedList$: Observable<NamedOption<number>[]>;

    /** Saved mention insight prompts, led by the blank one a new custom analysis is written into */
    customAnalysisOptions: DropdownOptionModel[] = [];

    /** Set while the Add New Action card is open; null the rest of the time */
    draftType: CurationActionTypeEnum = null;
    /** Which list the draft belongs to. New actions start on the matching side, as the design does */
    draftOutcome: CurationOutcomeEnum = CurationOutcomeEnum.matching;
    /** Index of the action being edited, or null when adding a new one */
    editingIndex: number = null;
    /** The list {@link editingIndex} counts within */
    editingOutcome: CurationOutcomeEnum = null;
    showAddCard: boolean = false;

    selectedFolders: Folder[] = [];
    selectedTags: TagModel[] = [];
    selectedRssFeed: NamedOption<number> = null;

    /** Generate Insight: which insight to run, and what it is written in */
    draftInsightType: MentionInsightTypes = null;
    draftLanguage: string = defaultInsightLanguage;
    /** The context the insight is written about, on the types that read one */
    draftKeywords: string[] = [];
    /** The custom prompt behind a Custom insight. A copy, so editing it leaves the saved one alone */
    selectedCustomAnalysis: CustomInsights = null;
    savingCustomAnalysis: boolean = false;

    folderChips: ChipModel[] = [];
    tagChips: ChipModel[] = [];

    folderCustomOptions: AutocompleteCustomOption[] = [
        {
            label: 'Mention.editDialog.folder.createNewFolder',
            action: (name: string) => this.createFolder(name),
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    tagCustomOptions: AutocompleteCustomOption[] = [
        {
            label: 'AutomationAgents.panel.actions.tag.create',
            action: (name: string) => this.createTag(name),
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    // Autocomplete writes the raw selection back through ngModel; kept separate from the chip lists
    folderSelection: Folder[] = [];
    tagSelection: TagModel[] = [];
    /** Editing holds one target, so its picker is a single autocomplete bound here instead */
    folderPick: Folder = null;
    tagPick: TagModel = null;

    private readonly subscriptions: Subscription[] = [];

    constructor(
        private folderService: FolderService,
        private tagsService: TagsService,
        private rssFeedService: RSSFeedService,
        private coverageAnalysisService: CoverageAnalysisService,
        private coreDialogService: CoreDialogService,
        private localizationService: LocalizationService
    ) {}

    ngOnInit(): void {
        this.folderList$ = this.folderService.all$;
        this.tagList$ = this.tagsService.all$;
        this.folderSuggestions$ = this.folderList$;
        this.tagSuggestions$ = this.tagList$;

        // Rebuilt rather than piped: a prompt created from the manager, or by this card, has to
        // show up in the list straight away
        this.subscriptions.push(
            this.coverageAnalysisService.customInsights$.subscribe(insights => {
                const blank = new CustomInsights();
                blank.promptType = CustomInsightType.MENTION_INSIGHTS;

                this.customAnalysisOptions = [
                    new DropdownOptionModel('AutomationAgents.panel.actions.customAnalysis.new', blank),
                    ...(insights ?? [])
                        .filter(insight => insight.promptType === CustomInsightType.MENTION_INSIGHTS)
                        .map(insight => new DropdownOptionModel(insight.name, insight.clone()))
                ];
            })
        );

        this.rssFeedList$ = this.rssFeedService.all$.pipe(
            map((feeds: RSSFeed[]) => (feeds ?? []).map(feed => ({ id: feed.id, name: feed.name })))
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    get needsFolders(): boolean {
        return folderActionTypes.includes(this.draftType);
    }

    get needsTags(): boolean {
        return tagActionTypes.includes(this.draftType);
    }

    get draftIsValid(): boolean {
        // The custom path can be valid before it can be built: a new analysis has no id until it
        // has been saved, which only happens once Add is pressed
        return this.draftType === CurationActionTypeEnum.generateInsight
            ? this.insightDraftIsValid
            : this.buildDraft().length > 0;
    }

    get isCustomInsight(): boolean {
        return this.draftInsightType === MentionInsightTypes.CUSTOM;
    }

    /** Whether the chosen custom analysis is one being written here rather than one already saved */
    get isNewCustomAnalysis(): boolean {
        return this.isCustomInsight && !!this.selectedCustomAnalysis && !this.selectedCustomAnalysis.id;
    }

    /** Whether the insight being drafted is one of the two that take a context */
    get draftReadsContext(): boolean {
        return contextReadingInsightTypes.includes(this.draftInsightType);
    }

    /** Everything below the Insight Type dropdown, which only some of the types need */
    get hasInsightSettings(): boolean {
        return !!this.draftInsightType;
    }

    private get insightDraftIsValid(): boolean {
        if (!this.draftInsightType) {
            return false;
        }

        if (!this.isCustomInsight) {
            return true;
        }

        if (!this.selectedCustomAnalysis) {
            return false;
        }

        return this.selectedCustomAnalysis.id
            ? true
            : !!this.selectedCustomAnalysis.name?.trim() && !!this.selectedCustomAnalysis.prompt?.trim();
    }

    /**
     * Saving to a folder or a tag also puts the mention on the account, so the outcome is called out
     * once above the lists rather than repeated on each of those cards.
     */
    get savesToAccount(): boolean {
        return [...this.positiveActions, ...this.negativeActions]
            .some(action => savesToAccountActionTypes.includes(action.type));
    }

    /** The list an outcome stands for. Returned by reference: the cards edit it in place */
    actionsFor(outcome: CurationOutcomeEnum): CurationAgentActionModel[] {
        return outcome === CurationOutcomeEnum.matching ? this.positiveActions : this.negativeActions;
    }

    outcomeLabel(outcome: CurationOutcomeEnum): string {
        return curationOutcomeLabels[outcome];
    }

    isEditing(outcome: CurationOutcomeEnum, index: number): boolean {
        return this.editingOutcome === outcome && this.editingIndex === index;
    }

    openAddCard(outcome: CurationOutcomeEnum): void {
        this.editingIndex = null;
        this.editingOutcome = null;
        this.draftOutcome = outcome;
        this.resetDraft();
        this.refreshActionTypeOptions();
        this.showAddCard = true;
        this.addCardOpened.emit();
    }

    /** Expands the card in place into the editor; the Add New Action card closes so only one form is open */
    edit(outcome: CurationOutcomeEnum, index: number): void {
        const action = this.actionsFor(outcome)[index];

        this.showAddCard = false;
        this.editingIndex = index;
        this.editingOutcome = outcome;
        this.resetDraft();
        this.draftType = action.type;
        this.draftOutcome = outcome;
        this.refreshActionTypeOptions();
        this.refreshTargetSuggestions();

        // Editing works on a single target, so the picker preselects the action's own one
        if (folderActionTypes.includes(action.type)) {
            this.folderList$.pipe(take(1)).subscribe(folders => {
                this.folderPick = (folders ?? []).find(folder => String(folder.id) === action.referenceId) ?? null;
                this.selectedFolders = this.folderPick ? [this.folderPick] : [];
            });
        }

        if (tagActionTypes.includes(action.type)) {
            this.tagList$.pipe(take(1)).subscribe(tags => {
                this.tagPick = (tags ?? []).find(tag => String(tag.id) === action.referenceId) ?? null;
                this.selectedTags = this.tagPick ? [this.tagPick] : [];
            });
        }

        if (action.type === CurationActionTypeEnum.generateInsight) {
            this.draftLanguage = action.language ?? defaultInsightLanguage;
            this.draftKeywords = [...(action.keywords ?? [])];

            if (action.insightType) {
                this.draftInsightType = action.insightType;
            } else {
                // No prebuilt type means the custom path, with the reference holding its prompt id
                this.draftInsightType = MentionInsightTypes.CUSTOM;
                this.selectedCustomAnalysis = this.customAnalysisOptions
                    .map(option => option.value as CustomInsights)
                    .find(analysis => String(analysis.id) === action.referenceId) ?? null;
            }
        }

        if (action.type === CurationActionTypeEnum.sendToRss) {
            this.rssFeedList$.pipe(take(1)).subscribe(feeds => {
                this.selectedRssFeed = feeds.find(feed => String(feed.id) === action.referenceId) ?? null;
            });
        }

        this.checkForSaveAction();
    }

    remove(outcome: CurationOutcomeEnum, index: number): void {
        this.actionsFor(outcome).splice(index, 1);

        if (this.editingOutcome === outcome) {
            if (this.editingIndex === index) {
                this.closeEditor();
            } else if (this.editingIndex > index) {
                // The open editor belongs to a later action that has just shifted down
                this.editingIndex--;
            }
        }

        // A form still open can now offer the type, or the target, that was just removed
        if (this.showAddCard || this.editingIndex != null) {
            this.refreshActionTypeOptions();
            this.refreshTargetSuggestions();
        }

        this.checkForSaveAction();
        this.emitChange();
    }

    typeChanged(type: CurationActionTypeEnum): void {
        this.draftType = type;
        this.clearDraftSelections();
        this.refreshTargetSuggestions();
    }

    insightTypeChanged(insightType: MentionInsightTypes): void {
        this.draftInsightType = insightType;
        this.selectedCustomAnalysis = null;
    }

    /**
     * Each target picked becomes its own action, so two folders are two actions. Editing replaces
     * the action in place, keeping its id so the service updates the row it already has.
     */
    commitDraft(): void {
        if (this.isNewCustomAnalysis) {
            this.saveCustomAnalysis();

            return;
        }

        this.commitResolvedDraft();
        this.checkForSaveAction();
    }

    checkForSaveAction(): void {
        [this.positiveActions, this.negativeActions].forEach(actions => {
            // Remove auto added save to account action if an explicit save action is added
            if (actions.find(action => action.autoAdded)
                && actions.find(action => action.type === CurationActionTypeEnum.saveToAccount || action.type === CurationActionTypeEnum.saveToFolder)
            ) {
                actions.splice(actions.findIndex(action => action.autoAdded), 1);
            }

            // Add auto save to account action if no explicit save action, but another action which requires saving is present
            if (
                !actions.find(action => action.type === CurationActionTypeEnum.saveToAccount || action.type === CurationActionTypeEnum.saveToFolder)
                && actions.find(action => action.type === CurationActionTypeEnum.generateInsight || action.type === CurationActionTypeEnum.saveToTag)
            ) {
                actions.push(this.addSaveToAccountAction());
            }
        });
        this.emitChange();
    }

    addSaveToAccountAction(): CurationAgentActionModel {
        const action = new CurationAgentActionModel();
        action.type = CurationActionTypeEnum.saveToAccount;
        action.autoAdded = true;
        return action;
    }

    /**
     * Writes the analysis being drafted here as a mention insight prompt, then carries on with the
     * action - which needs its id, so there is nothing to point at until this has come back.
     */
    private saveCustomAnalysis(): void {
        const analysis = this.selectedCustomAnalysis;

        analysis.name = analysis.name.trim();
        analysis.prompt = analysis.prompt.trim();
        analysis.promptType = CustomInsightType.MENTION_INSIGHTS;

        this.savingCustomAnalysis = true;
        this.coverageAnalysisService.createCustomInsight(analysis, InsightContext.CustomPromptDialog).pipe(
            take(1)
        ).subscribe({
            next: id => {
                this.savingCustomAnalysis = false;

                if (!id) {
                    return;
                }

                analysis.id = id;
                this.commitResolvedDraft();
            },
            error: () => this.savingCustomAnalysis = false
        });
    }

    private commitResolvedDraft(): void {
        const drafts = this.buildDraft();

        if (!drafts.length) {
            return;
        }

        const target = this.actionsFor(this.draftOutcome);

        if (this.editingIndex == null) {
            target.push(...drafts);
        } else {
            const editing = this.actionsFor(this.editingOutcome);
            const existing = editing[this.editingIndex];

            // Reuse the id when the row is still the same kind of action, so the API updates it -
            // including its targets - rather than deleting the row and creating another
            if (existing.type === drafts[0].type) {
                drafts[0].id = existing.id;
            }

            if (this.draftOutcome === this.editingOutcome) {
                editing.splice(this.editingIndex, 1, ...drafts);
            } else {
                // Moved to the other outcome. The id belongs to the row it is leaving, so it is
                // dropped: the service deletes that one and creates a new one on the other trigger
                drafts.forEach(draft => draft.id = null);
                editing.splice(this.editingIndex, 1);
                target.push(...drafts);
            }
        }

        this.closeEditor();
        this.emitChange();
    }

    private refreshActionTypeOptions(): void {
        const target = this.actionsFor(this.draftOutcome);

        this.actionTypeOptions = this.allActionTypeOptions.filter(option => !this.isDuplicateType(target, option.value));
    }

    /**
     * Saving to or deleting from the account happens once per outcome: a second one only repeats
     * the first, so the type is not offered again. Every other type can be added as often as wanted.
     * The row being edited does not count against itself, nor does the save the panel adds on its
     * own - an explicit one replaces it.
     */
    private isDuplicateType(target: CurationAgentActionModel[], type: CurationActionTypeEnum): boolean {
        if (!singleUseActionTypes.includes(type)) {
            return false;
        }

        const editing = this.editingRow(target);

        return target.some(action => action !== editing && !action.autoAdded && action.type === type);
    }

    /**
     * A folder or tag another action of the draft's type already targets in this outcome would
     * only repeat it, so the pickers leave those out. The row being edited keeps its own target.
     */
    private refreshTargetSuggestions(): void {
        const target = this.actionsFor(this.draftOutcome);
        const editing = this.editingRow(target);
        const usedIds = new Set(
            target
                .filter(action => action !== editing && action.type === this.draftType)
                .flatMap(action => action.referenceIds)
        );

        this.folderSuggestions$ = this.folderList$.pipe(
            map(folders => (folders ?? []).filter(folder => !usedIds.has(String(folder.id))))
        );
        this.tagSuggestions$ = this.tagList$.pipe(
            map(tags => (tags ?? []).filter(tag => !usedIds.has(String(tag.id))))
        );
    }

    /** The action open in the editor when it sits in the given list, otherwise null */
    private editingRow(target: CurationAgentActionModel[]): CurationAgentActionModel {
        return this.editingIndex != null && this.editingOutcome === this.draftOutcome
            ? target[this.editingIndex]
            : null;
    }

    folderPicked(): void {
        this.selectedFolders = this.folderPick ? [this.folderPick] : [];
    }

    tagPicked(): void {
        this.selectedTags = this.tagPick ? [this.tagPick] : [];
    }

    folderSelected(): void {
        this.selectedFolders = [...(this.folderSelection ?? [])];
        this.refreshFolderChips();
    }

    tagSelected(): void {
        this.selectedTags = [...(this.tagSelection ?? [])];
        this.refreshTagChips();
    }

    removeFolderChip(index: number): void {
        this.selectedFolders.splice(index, 1);
        this.folderSelection = [...this.selectedFolders];
        this.refreshFolderChips();
    }

    removeTagChip(index: number): void {
        this.selectedTags.splice(index, 1);
        this.tagSelection = [...this.selectedTags];
        this.refreshTagChips();
    }

    /** Newly created prompts become selectable as soon as customInsights$ refreshes behind the dialog */
    openInsightManager(): void {
        this.coreDialogService.open(CustomPromptsManagementDialogComponent, {
            inputs: {
                animationTrigger: this.slideInTrigger(),
                overlayContainer: this.overlayContainer,
                selectedPromptType: CustomInsightType.MENTION_INSIGHTS
            }
        });
    }

    /** Newly created feeds become selectable as soon as the feed list refreshes behind the dialog */
    openRssFeedManager(): void {
        this.coreDialogService.open(RSSFeedManageDialogComponent, {
            inputs: {
                animationTrigger: this.slideInTrigger(),
                overlayContainer: this.overlayContainer
            }
        });
    }

    private slideInTrigger(): BehaviorSubject<FullProfileAnimationModel> {
        return new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
    }

    /** One action per selected target, so a three folder selection is three actions */
    private buildDraft(): CurationAgentActionModel[] {
        if (!this.draftType) {
            return [];
        }

        if (this.needsFolders) {
            return this.selectedFolders.map(folder =>
                CurationAgentActionModel.forType(this.draftType, [String(folder.id)], [folder.name])
            );
        }

        if (this.needsTags) {
            return this.selectedTags.map(tag =>
                CurationAgentActionModel.forType(this.draftType, [String(tag.id)], [tag.name])
            );
        }

        if (this.draftType === CurationActionTypeEnum.generateInsight) {
            if (this.isCustomInsight) {
                const analysis = this.selectedCustomAnalysis;

                // The service takes exactly one prompt id here - another prompt is another action
                return analysis?.id
                    ? [CurationAgentActionModel.forType(this.draftType, [String(analysis.id)], [analysis.name])]
                    : [];
            }

            return this.draftInsightType
                ? [CurationAgentActionModel.forInsightType(
                    this.draftInsightType,
                    this.draftLanguage,
                    this.insightTypeLabel(this.draftInsightType),
                    this.draftReadsContext ? this.draftKeywords : []
                )]
                : [];
        }

        if (this.draftType === CurationActionTypeEnum.sendToRss) {
            return this.selectedRssFeed
                ? [CurationAgentActionModel.forType(
                    this.draftType,
                    [String(this.selectedRssFeed.id)],
                    [this.selectedRssFeed.name]
                )]
                : [];
        }

        return [CurationAgentActionModel.forType(this.draftType)];
    }

    private emitChange(): void {
        this.actionsChanged.emit({ positive: this.positiveActions, negative: this.negativeActions });
    }

    protected closeEditor(): void {
        this.showAddCard = false;
        this.editingIndex = null;
        this.editingOutcome = null;
        this.resetDraft();
    }

    private resetDraft(): void {
        this.draftType = null;
        this.clearDraftSelections();
    }

    private clearDraftSelections(): void {
        this.selectedFolders = [];
        this.selectedTags = [];
        this.folderSelection = [];
        this.tagSelection = [];
        this.folderPick = null;
        this.tagPick = null;
        this.selectedRssFeed = null;
        this.draftInsightType = null;
        this.draftLanguage = defaultInsightLanguage;
        this.draftKeywords = [];
        this.selectedCustomAnalysis = null;
        this.folderChips = [];
        this.tagChips = [];
    }

    /** What the card reads for a prebuilt insight, from the same list the dropdown offers */
    private insightTypeLabel(insightType: MentionInsightTypes): string {
        const option = this.insightTypeOptions.find(item => item.value === insightType);

        return option ? this.localizationService.transform(option.label) : '';
    }

    private refreshFolderChips(): void {
        this.folderChips = this.selectedFolders.map(folder => ({ label: folder.name, id: folder.id }));
    }

    private refreshTagChips(): void {
        this.tagChips = this.selectedTags.map(tag => ({ label: tag.name, id: tag.id }));
    }

    private createFolder(name: string): void {
        const folder = new Folder();
        folder.name = name;

        this.folderService.create(folder).pipe(
            switchMap(id => this.folderList$.pipe(map(folders => folders.find(item => item.id === id)))),
            take(1)
        ).subscribe(created => {
            if (!created) {
                return;
            }
            if (this.editingIndex != null) {
                this.folderPick = created;
                this.folderPicked();

                return;
            }
            this.selectedFolders = [...this.selectedFolders, created];
            this.folderSelection = [...this.selectedFolders];
            this.refreshFolderChips();
        });
    }

    private createTag(name: string): void {
        const tag = new TagModel();
        tag.name = name;

        this.tagsService.createTag(tag).pipe(
            switchMap(id => this.tagsService.getTagById(id)),
            take(1)
        ).subscribe(created => {
            if (!created) {
                return;
            }
            if (this.editingIndex != null) {
                this.tagPick = created;
                this.tagPicked();

                return;
            }
            this.selectedTags = [...this.selectedTags, created];
            this.tagSelection = [...this.selectedTags];
            this.refreshTagChips();
        });
    }
}
